import React from "react";

import { useTranslation, useUserContext } from "utils";

import { COUNTRY_CODE } from "consts";

import { Button, Flex, Icon, Typography, WidgetVideo } from "components";

import { HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL } from "pages/app/historical-practice-data-table-view/HistoricalPracticeDataTableView";

export type ImpactPanelEmptyStateVariant = "default" | "currentPeriod";

type ImpactPanelEmptyStateProps = {
  variant?: ImpactPanelEmptyStateVariant;
  onStartAddingData: () => void;
};

const VIDEO_COUNTRIES = new Set([COUNTRY_CODE.GERMANY]);

const BENEFITS = ["enrollFields", "provideBaselineAndYearly", "seePayoutPrediction"];

export const ImpactPanelEmptyState = ({ variant = "default", onStartAddingData }: ImpactPanelEmptyStateProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { user } = useUserContext();

  const isCurrentPeriod = variant === "currentPeriod";
  const translationPrefix = isCurrentPeriod ? "impactPanel.emptyState.currentPeriod" : "impactPanel.emptyState";
  const testIdPrefix = isCurrentPeriod ? "impact-panel-current-period-empty-state" : "impact-panel-empty-state";

  const showVideo = !isCurrentPeriod && !!user?.farm?.country && VIDEO_COUNTRIES.has(user.farm.country as COUNTRY_CODE);

  return (
    <div className="impact-panel-empty-state" data-testid={testIdPrefix}>
      {/* Guidance Section */}
      <Flex vertical gap="1.5rem" className="impact-panel-empty-state-guidance">
        <div>
          <Typography variant="h3" className="impact-panel-empty-state-title">
            {t(`${translationPrefix}.title`)}
          </Typography>
          <Typography variant="paragraph-m" className="impact-panel-empty-state-description">
            {t(`${translationPrefix}.description`)}
          </Typography>
        </div>
        {showVideo && (
          <WidgetVideo
            vimeoUrlOrId={HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL}
            title={t("impactPanel.emptyState.video.title")}
            description={t("impactPanel.emptyState.video.description")}
            allowedCountries={Array.from(VIDEO_COUNTRIES)}
          />
        )}
        {/* Benefits list */}
        {!isCurrentPeriod && (
          <Flex vertical gap="0.75rem" className="impact-panel-empty-state-benefits">
            {BENEFITS.map((key) => (
              <Flex key={key} align="center" gap="0.5rem">
                <Icon iconName="tabler/check" size="s" className="impact-panel-empty-state-check-icon" />
                <span className="paragraph-s">{t(`impactPanel.emptyState.benefits.${key}`)}</span>
              </Flex>
            ))}
          </Flex>
        )}
        <Button type="primary" onClick={onStartAddingData} className="impact-panel-empty-state-cta" dataTestId={`${testIdPrefix}-cta`}>
          {t(`${translationPrefix}.cta`)}
        </Button>
      </Flex>
    </div>
  );
};
